import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, CheckSquare, Calendar as CalendarIcon, Users, Loader2, Check, X, AtSign, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { parseServerDate } from '../utils/dateUtils';
import { usePageHeader } from '../context/PageHeaderContext';
import PageLoader from '../components/PageLoader';

const ICON_MAP = {
  task_assigned: CheckSquare,
  appointment_reminder_7d: CalendarIcon,
  appointment_reminder_2d: CalendarIcon,
  appointment_reminder_2h: CalendarIcon,
  contact_submission: Users,
  meeting_request: CalendarIcon,
  meeting_accepted: CalendarIcon,
  meeting_declined: CalendarIcon,
  memo_mention: AtSign,
  memo_team_invite: UserPlus,
};

const COLOR_MAP = {
  task_assigned: '#3B82F6',
  appointment_reminder_7d: '#10B981',
  appointment_reminder_2d: '#10B981',
  appointment_reminder_2h: '#F59E0B',
  contact_submission: '#8B5CF6',
  meeting_request: '#586D52',
  meeting_accepted: '#10B981',
  meeting_declined: '#EF4444',
  memo_mention: '#586D52',
  memo_team_invite: '#757150',
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tracks which meeting_request notifications are "in flight"
  // (Accept/Decline button click) so we can disable both buttons.
  const [respondingTo, setRespondingTo] = useState(null);
  const [meetingMeta, setMeetingMeta] = useState({}); // related_id → meeting doc

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/app-notifications');
      const list = res.data.items || [];
      setItems(list);
      // For meeting_request rows, fetch the underlying request shape
      // so we can show the start/end + actions inline.
      const meetingIds = list
        .filter((n) => n.type === 'meeting_request' && n.related_id)
        .map((n) => n.related_id);
      if (meetingIds.length) {
        try {
          const inboxRes = await api.get('/meeting-requests/inbox');
          const map = {};
          (inboxRes.data?.requests || []).forEach((r) => { map[r.id] = r; });
          setMeetingMeta(map);
        } catch (e) { /* silent — fall back to title/body only */ }
      }
    } catch (e) {
      // silent
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const markAll = async () => {
    try {
      // iter123 — Hard-clear: deletes every notification for the
      // caller so they don't re-appear in the next session. The
      // legacy mark-as-read behavior wasn't enough — read rows still
      // showed up in the list and felt like "ghost" notifications.
      await api.post('/app-notifications/clear-all');
      setItems([]);
      // Tell the Layout's bell badge to snap to 0 IMMEDIATELY (and
      // also refetch as a safety net).
      window.dispatchEvent(new CustomEvent('app-notifications-changed', { detail: { count: 0 } }));
      toast.success('Notifications cleared');
    } catch (e) {
      toast.error('Could not clear notifications');
    }
  };

  // Hoist the page title + back arrow + "Clear all" action into the
  // global app header (matches all other pages). Re-runs whenever the
  // item count changes so the "Clear all" button appears/hides.
  usePageHeader({
    title: 'Notifications',
    back: () => navigate(-1),
    rightSlot: items.length > 0 ? (
      <button
        onClick={markAll}
        className="text-xs font-semibold text-[#586D52] hover:underline px-2"
        data-testid="notifications-mark-all"
      >
        Clear all
      </button>
    ) : null,
  }, [items.length]);

  const markRead = async (n) => {
    // Meeting request rows are interactive — don't auto-navigate.
    if (n.type === 'meeting_request') {
      if (!n.is_read) {
        try { await api.post(`/app-notifications/${n.id}/read`); } catch (e) {}
        setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
        window.dispatchEvent(new CustomEvent('app-notifications-changed'));
      }
      return;
    }
    if (!n.is_read) {
      try { await api.post(`/app-notifications/${n.id}/read`); } catch (e) {}
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, is_read: true } : x));
      window.dispatchEvent(new CustomEvent('app-notifications-changed'));
    }
    // iter108 — admin override-request notifications open a review
    // popup (intermediate "View Request" step), NOT a direct navigate
    // into the edit user dialog. Approve from the popup deep-links;
    // Reject is handled inline.
    if (n.type === 'admin_override_request' && n.related_id) {
      window.dispatchEvent(new CustomEvent('open-admin-override-review', {
        detail: { override_request_id: n.related_id },
      }));
      return;
    }
    if (n.link) navigate(n.link);
  };

  const respondMeeting = async (n, action) => {
    if (!n.related_id) return;
    setRespondingTo(n.id);
    try {
      await api.post(`/meeting-requests/${n.related_id}/respond`, { action });
      toast.success(action === 'accept' ? 'Meeting accepted — added to your calendar' : 'Meeting declined');
      // Refresh so the row falls off (server marks request status).
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.error(detail || 'You now have a calendar conflict');
      } else {
        toast.error(detail || 'Could not respond to meeting');
      }
    } finally {
      setRespondingTo(null);
    }
  };

  const renderMeetingActions = (n) => {
    const req = meetingMeta[n.related_id];
    const start = parseServerDate(req?.start_time);
    const end = parseServerDate(req?.end_time);
    const range = (start && end)
      ? `${start.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} – ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
      : null;
    return (
      <div className="mt-2 space-y-2">
        {range && (
          <p className="text-xs font-medium text-[#586D52]" data-testid={`meeting-time-${n.id}`}>
            {range}
          </p>
        )}
        <div className="flex gap-2">
          <button
            disabled={respondingTo === n.id}
            onClick={(e) => { e.stopPropagation(); respondMeeting(n, 'accept'); }}
            className="flex-1 inline-flex items-center justify-center gap-1 bg-[#586D52] hover:bg-[#4a5d45] text-white text-xs font-semibold rounded-lg py-1.5 disabled:opacity-50"
            data-testid={`meeting-accept-${n.id}`}
          >
            {respondingTo === n.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Accept
          </button>
          <button
            disabled={respondingTo === n.id}
            onClick={(e) => { e.stopPropagation(); respondMeeting(n, 'decline'); }}
            className="flex-1 inline-flex items-center justify-center gap-1 border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 text-xs font-semibold rounded-lg py-1.5 disabled:opacity-50"
            data-testid={`meeting-decline-${n.id}`}
          >
            {respondingTo === n.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            Decline
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }} data-testid="notifications-page">
      <div className="max-w-4xl mx-auto">
      {loading ? (
        <PageLoader label="Loading notifications..." fullScreen={false} gradient={false} testId="notifications-loading" />
      ) : items.length === 0 ? (
        <div className="text-center text-muted-foreground py-16">
          <Bell className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No notifications yet</p>
          <p className="text-xs mt-1">You&apos;ll see task, appointment and contact alerts here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const Icon = ICON_MAP[n.type] || Bell;
            const color = COLOR_MAP[n.type] || '#586D52';
            const isMeeting = n.type === 'meeting_request' && meetingMeta[n.related_id];
            return (
              <div
                key={n.id}
                className={`w-full text-left rounded-xl bg-white dark:bg-[#1a1a1a] shadow-sm p-3 flex items-start gap-3 transition-colors hover:shadow-md ${
                  n.is_read ? 'opacity-70' : 'border-l-4'
                } ${n.type !== 'meeting_request' ? 'cursor-pointer' : ''}`}
                style={!n.is_read ? { borderLeftColor: color } : {}}
                onClick={() => markRead(n)}
                data-testid={`notification-card-${n.id}`}
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
                  <Icon className="w-4 h-4" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold leading-snug">{n.title}</p>
                  {n.message && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                  {isMeeting && renderMeetingActions(n)}
                </div>
                {!n.is_read && (
                  <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" aria-label="unread" />
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
